import type { OrgTreeServiceInstance } from '@/services/org-tree'

export type NodeServicePermissionOption = {
  key: string
  instanceId: string
  variantId: string
  variantLabel: string
  instanceName: string
}

export function getServiceInstanceVariantDisplayName(
  instance: {
    service_variant_name?: string | null
    service_variant_label?: string | null
    service_variant_type?: string | null
    service_variant_service?: string | null
    service_variant?: string | number | null
    service_data?: OrgTreeServiceInstance['service_data']
  },
): string {
  const variantLabel = String(
    instance.service_variant_name
    ?? instance.service_variant_type
    ?? instance.service_variant_label
    ?? '',
  ).trim()

  if (variantLabel) {
    return variantLabel
  }

  return 'Service'
}

export function buildNodeServicePermissionOptions(
  instances: OrgTreeServiceInstance[],
): NodeServicePermissionOption[] {
  const byInstanceId = new Map<string, NodeServicePermissionOption>()

  for (const instance of instances) {
    const instanceId = String(instance.instance_id ?? instance.id ?? '').trim()
    if (!instanceId || byInstanceId.has(instanceId)) {
      continue
    }

    const variantId = String(
      instance.service_variant
      ?? instance.service_data?.variant_unique
      ?? '',
    ).trim()
    const variantLabel = getServiceInstanceVariantDisplayName(instance)
    const instanceName = String(instance.name ?? instance.title ?? variantLabel).trim() || variantLabel

    byInstanceId.set(instanceId, {
      key: instanceId,
      instanceId,
      variantId,
      variantLabel,
      instanceName,
    })
  }

  return Array.from(byInstanceId.values()).sort((first, second) => (
    first.variantLabel.localeCompare(second.variantLabel)
    || first.instanceName.localeCompare(second.instanceName)
  ))
}

type PostWorkspaceInstanceSource = {
  node_id?: string | number | null
  service_instance?: string | number | null
  service_instance_id?: string | number | null
  service_instance_name?: string | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  service_variant_unique_id?: string | number | null
}

export function buildNodeServicePermissionOptionsFromPosts(
  posts: PostWorkspaceInstanceSource[] | null | undefined,
  nodeId?: string | number | null,
): NodeServicePermissionOption[] {
  const normalizedNodeId = String(nodeId ?? '').trim()
  if (!normalizedNodeId || !posts?.length) {
    return []
  }

  const instances = posts
    .filter((post) => String(post.node_id ?? '').trim() === normalizedNodeId)
    .map((post) => ({
      instance_id: String(post.service_instance ?? post.service_instance_id ?? '').trim(),
      service_variant: post.service_variant_unique_id,
      service_variant_name: post.service_variant_name ?? post.service_variant_type,
      name: post.service_instance_name,
    }))
    .filter((instance) => Boolean(String(instance.instance_id ?? '').trim())) as OrgTreeServiceInstance[]

  return buildNodeServicePermissionOptions(instances)
}

export function mergeNodeServicePermissionOptions(
  ...optionGroups: NodeServicePermissionOption[][]
): NodeServicePermissionOption[] {
  const byInstanceId = new Map<string, NodeServicePermissionOption>()

  for (const options of optionGroups) {
    for (const option of options) {
      if (!option.instanceId || byInstanceId.has(option.instanceId)) {
        continue
      }
      byInstanceId.set(option.instanceId, option)
    }
  }

  return Array.from(byInstanceId.values()).sort((first, second) => (
    first.variantLabel.localeCompare(second.variantLabel)
    || first.instanceName.localeCompare(second.instanceName)
  ))
}

export function resolveNodeServicePermissionOption(
  options: NodeServicePermissionOption[],
  instanceId?: string | null,
): NodeServicePermissionOption | null {
  const normalizedInstanceId = String(instanceId ?? '').trim()
  if (!normalizedInstanceId) {
    return options[0] ?? null
  }

  return options.find((option) => option.instanceId === normalizedInstanceId) ?? options[0] ?? null
}
